import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Users } from 'lucide-react';

const ChatNotification = ({ notification, onClose, onClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 50);
    
    // Auto dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleClick = () => {
    onClick && onClick();
    handleClose();
  };

  if (!notification) return null;

  const isGroup = notification.type === 'group';

  return (
    <div
      className={`fixed top-4 right-4 z-[9999] max-w-sm w-full transform transition-all duration-300 ease-out ${
        isVisible && !isLeaving 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
      }`}
    >
      <div 
        onClick={handleClick}
        className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden cursor-pointer hover:shadow-3xl transition-shadow group"
      >
        {/* Gradient accent bar */}
        <div className={`h-1 ${isGroup ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gradient-to-r from-blue-500 to-emerald-500'}`} />
        
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shrink-0 ${
              isGroup 
                ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                : 'bg-gradient-to-br from-blue-500 to-emerald-500'
            }`}>
              {isGroup ? (
                <Users className="w-6 h-6" />
              ) : (
                notification.senderName?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-slate-900 truncate">
                  {notification.senderName || 'New Message'}
                </h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {isGroup && (
                <p className="text-xs text-purple-600 font-medium mb-0.5">
                  {notification.groupName}
                </p>
              )}
              
              <p className="text-sm text-slate-600 line-clamp-2">
                {notification.message}
              </p>
              
              <p className="text-xs text-slate-400 mt-1.5">
                Just now
              </p>
            </div>
          </div>
          
          {/* Action hint */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-400">Click to open chat</span>
            <div className="flex items-center gap-1 text-blue-500">
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">Reply</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification container for multiple notifications
const ChatNotificationContainer = ({ notifications, onClose, onNotificationClick }) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3">
      {notifications.map((notif, index) => (
        <div 
          key={notif.id || index}
          style={{ transform: `translateY(${index * 8}px)` }}
        >
          <ChatNotification
            notification={notif}
            onClose={() => onClose(notif.id || index)}
            onClick={() => onNotificationClick && onNotificationClick(notif)}
          />
        </div>
      ))}
    </div>
  );
};

export { ChatNotification, ChatNotificationContainer };
export default ChatNotification;
